import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { clientApi } from '@/config/axios';
import { usePermissions } from '@/features/permissions';
import { Button } from '@/components/ui/button';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { actionError } from '@/features/agent-proposals/IntakeReview';

type Settings = {
  expected_version: number;
  enabled: boolean;
  reviewer_user_id: string;
  manual_only: boolean;
  provisional_intake: boolean;
  total_run_cap: number;
  daily_run_cap: number;
  rules: unknown[];
};
export function IncomingSettings({ org }: { org: string }) {
  const { can } = usePermissions();
  const [open, setOpen] = useState(false);
  const settings = useQuery({
    enabled: open && can('manage_agent_policy'),
    queryKey: ['assistant-intake-settings', org],
    queryFn: async () =>
      (await clientApi.get<Settings>(`/api/v1/organizations/${org}/assistant/incoming/settings`)).data,
  });
  if (!can('manage_agent_policy')) return null;
  return (
    <details onToggle={(e) => setOpen(e.currentTarget.open)}>
      <summary>Practice processing settings</summary>
      {settings.isError ? (
        <p role="alert">Settings could not be loaded.</p>
      ) : (
        settings.data && <SettingsForm key={settings.data.expected_version} org={org} initial={settings.data} />
      )}
    </details>
  );
}
function SettingsForm({ org, initial }: { org: string; initial: Settings }) {
  const [values, setValues] = useState(initial);
  const cache = useQueryClient();
  const save = useMutation({
    mutationFn: () => clientApi.put(`/api/v1/organizations/${org}/agent/policy`, values),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: ['assistant-intake-settings'] });
      void cache.invalidateQueries({ queryKey: ['assistant-incoming'] });
      void cache.invalidateQueries({ queryKey: ['agent-jobs'] });
    },
  });
  return (
    <FieldGroup>
      <p>
        These settings apply to this practice. Automatic preparation creates provisional charts only when the identity
        is clear and no possible existing patient is found. Clinical information still requires intake approval.
        Publishing changes can stop work running under the previous settings.
      </p>
      <Field orientation="horizontal">
        <input
          id="automatic-intake"
          type="checkbox"
          checked={!values.manual_only}
          onChange={(e) => setValues({ ...values, manual_only: !e.target.checked })}
        />
        <FieldLabel htmlFor="automatic-intake">Automatically analyze incoming saved text</FieldLabel>
      </Field>
      <Field orientation="horizontal">
        <input
          id="provisional-intake"
          type="checkbox"
          checked={values.provisional_intake}
          onChange={(e) => setValues({ ...values, provisional_intake: e.target.checked })}
        />
        <FieldLabel htmlFor="provisional-intake">Prepare provisional charts and patient associations</FieldLabel>
      </Field>
      <Field>
        <FieldLabel htmlFor="daily-cap">Daily processing limit</FieldLabel>
        <Input
          id="daily-cap"
          type="number"
          min={1}
          max={200}
          value={values.daily_run_cap}
          onChange={(e) => setValues({ ...values, daily_run_cap: Number(e.target.value) })}
        />
      </Field>
      <Field>
        <FieldLabel htmlFor="total-cap">Total pilot processing limit</FieldLabel>
        <Input
          id="total-cap"
          type="number"
          min={1}
          max={200}
          value={values.total_run_cap}
          onChange={(e) => setValues({ ...values, total_run_cap: Number(e.target.value) })}
        />
      </Field>
      <p>
        Server and practice processing switches must also be enabled. Reaching either limit pauses new analysis;
        affected faxes remain in the worklist.
      </p>
      <Button variant="outline" disabled={save.isPending} onClick={() => save.mutate()}>
        Save practice processing settings
      </Button>
      {save.isError && <p role="alert">{actionError(save.error, 'Settings were not saved.')}</p>}
    </FieldGroup>
  );
}
